import { Popover } from '@mui/material';
import {
  AlarmIconButton,
  ContentWrapper,
  Layout,
  StyledButton,
  StyledHeader,
  StyledNodata,
  StyledTitle,
} from '@/components/Alarm/styles';
import {
  useMessageAllRead,
  useNotiHistory,
  useNotiHistoryPagination,
} from '@/hooks/apiHooks';
import NotificationHistoryItem from '@/components/Alarm/NotificationHistoryItem';

import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { getIntranetDocUrl } from '@/utils/urls';
import useModal from '@/hooks/useModal';
import useApprovalData from '@/hooks/approval/useApprovalData';
import useApproval from '@/hooks/approval/useApproval';
import { ModalRef } from '@/components/@IntranetLibrary/Modal';
import ApprovalModal from '@/components/Approval/ApprovalModal';
import NextApprovalConfirmModal from '@/components/Approval/NextApprovalConfirmModal';
import useNextApprovalDoc from '@/components/Approval/@hooks/useNextApprovalDoc';
import { NATIVE_VIEW_APPROVAL_TYPES } from '@/constant';
import type { NextApprovalDoc } from '@/components/Approval/@hooks/useNextApprovalDoc';
import type { ApprovalDecisionAction } from '@/hooks/approval/useApprovalDecision';
import { overCountText } from '@/utils';
import { useGNBEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import { GNBEventName } from '@/constant/gtm';
import NotificationIcon from '@/public/icons/gnb_notification.svg';
import classNames from 'classnames';
import TalkFLowModal, {
  TalkFlowModalRef,
} from '@/components/Record/TalkFLowModal';

export const AlarmSuspense = () => {
  return (
    <AlarmIconButton>
      <NotificationIcon />
    </AlarmIconButton>
  );
};

const listSize = 50;

const Alarm = () => {
  const gnbEventTracker = useGNBEventTracker();
  const router = useRouter();
  const { data } = useNotiHistory(listSize);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const {
    data: paginationData,
    size,
    setSize,
    mutate,
  } = useNotiHistoryPagination(listSize);

  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const hasUnreadMessage = unreadMessageCount > 0;

  const noMoreData = (data?.meta.endPage || 0) <= size;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    gnbEventTracker(GNBEventName.clickNotification, {
      button_text: '새소식',
      contents: '새소식',
    });
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAllRead = () => {
    useMessageAllRead().then(() => mutate());
    setUnreadMessageCount(0);
  };

  const handleRead = async (id: number) => {
    if (!paginationData) {
      return;
    }

    let updatedUnreadCount = false;

    const afterData = paginationData.map((res) =>
      res.map((item) => {
        if (item.id === id) {
          updatedUnreadCount = item.watched === 0;

          return { ...item, watched: 1 as 0 | 1 };
        } else {
          return item;
        }
      }),
    );

    await mutate(afterData);
    setUnreadMessageCount((pre) => (updatedUnreadCount ? pre - 1 : pre));
  };

  useEffect(() => {
    setUnreadMessageCount(data?.meta?.unreadHistoryCount ?? 0);
  }, [data]);
  const { dataItemChange } = useApprovalData();

  const [docId, setDocId] = useState('');
  const approvalModalRef = useRef<ModalRef>(null);
  const recordDetailModalRef = useRef<TalkFlowModalRef>(null);
  const { openModal: openApprovalModal, closeModal: closeApprovalModal } =
    useModal(approvalModalRef);

  // 알림엔 품의 type 이 없어 단건조회로 type 을 확보해야 ApprovalModal 이 native(type 1) 분기를 탄다.
  const { data: approvalDetail, error: approvalDetailError } = useApproval(
    docId || null,
  );
  // 다음 문서로 스왑할 때 단건조회 응답 전까지 쓸 양식 코드 — 없으면 native 문서도 iframe 으로 잠깐 떴다 전환됨
  const [swappedType, setSwappedType] = useState<string>();
  const approvalType = approvalDetail
    ? String(approvalDetail.type.code)
    : swappedType;
  // type 미확정 상태로 열면 native 문서도 잠깐 iframe 으로 떴다 전환되므로 type/에러 확정 후 오픈.
  const pendingOpenRef = useRef(false);

  const isNativeType =
    !!approvalType && NATIVE_VIEW_APPROVAL_TYPES.includes(Number(approvalType));

  // 프리페치는 알림에서 품의를 연 뒤부터 — Alarm 은 GNB 하위라 모든 페이지에 상시 마운트돼
  // 게이팅 없이 부르면 품의와 무관한 화면에서도 계속 나감. 위 useApproval 과 같은 기준.
  // 레거시 양식은 다음 문서 선정도 레거시 몫 — 포탈이 고르면 큐가 어긋남
  const { nextDoc, promptNext, clearNextDoc } = useNextApprovalDoc(
    docId,
    !!docId && isNativeType,
  );

  /** 모달은 유지 — docId/양식 스왑으로 같은 모달에 다음 문서 로드 */
  const openDoc = (next: NextApprovalDoc) => {
    setSwappedType(
      next.typeCode === undefined ? undefined : String(next.typeCode),
    );
    setDocId(next.docId);
    clearNextDoc();
  };

  const onOpen = (clickedDocId: string) => {
    dataItemChange?.(clickedDocId, { read: 1 });
    setSwappedType(undefined); // 이전 흐름에서 스왑한 양식 코드가 새 문서에 붙지 않게
    clearNextDoc(); // 새로 여는 흐름 — 직전 결재의 응답이 늦게 와서 덮어쓰지 않게
    // 같은 문서 재클릭(이미 조회됨)은 state 변화가 없어 아래 effect 가 안 도므로 즉시 오픈.
    if (clickedDocId === docId && (approvalType || approvalDetailError)) {
      openApprovalModal();
      return;
    }
    pendingOpenRef.current = true;
    setDocId(clickedDocId);
  };

  /** 결재 완료 후 다음 결재 문서 레이어, 없으면 닫기 (목록 모달과 동일 동작) */
  const handleDecided = async (
    decidedDocId: string,
    action: ApprovalDecisionAction,
  ) => {
    // 보류(H)는 문서가 내 결재 큐에 남아 본인이 재처리 — 대상 아님
    if (action === 'hold') return;
    const { next, stale } = await promptNext(decidedDocId, action);
    if (!next && !stale) closeApprovalModal();
  };

  useEffect(() => {
    if (pendingOpenRef.current && (approvalType || approvalDetailError)) {
      pendingOpenRef.current = false;
      openApprovalModal();
    }
  }, [approvalType, approvalDetailError, openApprovalModal]);

  const openTalkFlowModal = (docId: string) => {
    recordDetailModalRef.current?.open(docId);
  };

  return (
    <>
      <AlarmIconButton
        aria-label={hasUnreadMessage ? '새 알림 있음' : '알림'}
        className={classNames({ new: hasUnreadMessage })}
        data-count={overCountText(unreadMessageCount)}
        onClick={handleClick}
      >
        <NotificationIcon />
      </AlarmIconButton>
      {router.isReady && (
        <>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Layout>
              <StyledHeader>
                <StyledTitle>NOTIFICATIONS</StyledTitle>
                <StyledButton type={'button'} onClick={handleAllRead}>
                  Mark all as read
                </StyledButton>
              </StyledHeader>
              <ContentWrapper>
                {paginationData && paginationData.flat().length > 0 ? (
                  paginationData
                    .flat()
                    .map((res, idx) => (
                      <NotificationHistoryItem
                        handleReadClick={handleRead}
                        key={`notification-${idx}`}
                        {...res}
                        handleOpen={onOpen}
                        openTalkFlowModal={openTalkFlowModal}
                        closePanel={handleClose}
                      />
                    ))
                ) : (
                  <StyledNodata>알람이 없습니다.</StyledNodata>
                )}
                {!noMoreData && (
                  <button
                    type={'button'}
                    onClick={() => setSize((_size) => _size + 1)}
                    style={{ padding: '1rem 0' }}
                  >
                    Load More
                  </button>
                )}
              </ContentWrapper>
            </Layout>
          </Popover>
          <ApprovalModal
            ref={approvalModalRef}
            url={getIntranetDocUrl(docId, true)}
            docId={docId}
            approvalType={approvalType}
            dataItemChange={dataItemChange}
            handleClose={() => {
              clearNextDoc(); // 결재 응답 대기 중 닫으면 뒤늦게 레이어가 뜸
              closeApprovalModal();
            }}
            onDecided={handleDecided}
            onOpenNextDoc={openDoc}
          />
          <NextApprovalConfirmModal
            open={!!nextDoc}
            onOpenNext={() => {
              if (!nextDoc) return;
              openDoc(nextDoc);
            }}
            onClose={() => {
              clearNextDoc();
              closeApprovalModal();
            }}
          />
          <TalkFLowModal ref={recordDetailModalRef} />
        </>
      )}
    </>
  );
};

export default Alarm;
